/**
 * The Stage's occupant — TWO CLASSES, resolved once for every host.
 *
 * The Stage is a SLOT in `anchor-driven-surfaces`' vocabulary: one focused
 * occupant, swappable, never simultaneous multi-video. What may sit in it is
 * settled by `PRODUCT_PLAN.md` §2.5b, read off `publisher-drop-hub.html`'s own
 * mock controls rather than assumed:
 *
 *   CLASS 1 · STREAMING     — WATCHING (swap, same footprint) and EXPANDED
 *                             (grows; the side content becomes chat).
 *   CLASS 2 · NON-STREAMING — an open widget slot: ad, info, promo, rotation.
 *
 * ── LIVE vs VOD IS A STATE, NOT A CLASS ──
 *
 * A VOD in the Stage is the Streaming class with `live: false`. This matters
 * because the original read of §2.5 flagged PAGE's VOD fallback as a
 * shortcoming — "no true idle state" — and it is not one. PAGE's existing
 * `StreamPanel` already falls back to a most-recent VOD through the same
 * player slot rather than a different mechanic, and that behaviour is correct.
 * Modelling VOD as a third class would have split one mechanic in two and
 * made PAGE's existing behaviour look like a bug to be fixed.
 *
 * ── WHY "IDENTITY" IS NOT THE NAME OF CLASS 2 ──
 *
 * The anchor labels its resting state "Identity", and that label is too narrow
 * to generalise from: it describes what that one anchor happened to put there
 * (Shelly, 2026-08-24) — key art, a countdown, Pre-order/Wishlist CTAs — not
 * the category. The category is "not streaming", and its occupant is drawn
 * from the widget catalogue like every other open extension point in this
 * layer model. Naming the class after the anchor's example would have frozen
 * one campaign's content choice into the type system.
 *
 * ── WHAT THIS FILE DOES NOT DO ──
 *
 * It resolves which occupant the Stage has and what state it is in. It does
 * NOT render — `SurfaceStage` (HUB) and `CreatorSection` (PAGE) keep their own
 * markup, exactly as the roster resolver leaves each host's card markup alone.
 * Tournament's `useTournamentPovs`/`TournamentStreamViewer` are evidence for
 * this shape and are deliberately NOT migrated onto it (`PRODUCT_PLAN.md` §9 /
 * D7) — generalising Tournament is out of scope for this build.
 */

import React from "react";
import {StageState} from "./occupancy";

/** Which class of thing is in the Stage. */
export enum StageOccupantClass {
    /** A live or recorded stream, played in place. */
    STREAMING = "STREAMING",
    /** Anything else — brand panel, ad, promo, rotation. An open widget slot. */
    NON_STREAMING = "NON_STREAMING",
}

/**
 * The Stage's size/behaviour state — `StageState` from `occupancy.ts`, REUSED.
 *
 * Not re-declared. This file originally defined its own three-member enum
 * whose "playing" member was `WATCHING` where `occupancy.ts` says `WATCH`, and
 * claimed in a comment to be avoiding a second vocabulary while being one:
 * `DropHubSurface` then carried a three-branch ternary translating between
 * them and `CreatorSection` compared against the local copy, so two consumers
 * had to know both spellings and keep the mapping in sync.
 *
 * `occupancy.ts` owns this vocabulary because the column-width maths keys on
 * it (`COLUMN_WIDTH_EXPANDED_PX`) and `HubStructure` publishes it as
 * `data-stage`. Re-exported here so a host resolving a Stage does not have to
 * import from two modules to do it.
 *
 * `EXPANDED` belongs only to the Streaming class: there is nothing for a brand
 * panel to expand INTO, because what expansion buys is room for chat beside a
 * stream.
 */
export {StageState} from "./occupancy";

/**
 * A stream in the Stage.
 *
 * `player` is a mounted node rather than a URL because the Stage exists to be
 * watched IN — a still with a play button that navigates away is a thumbnail,
 * not a stage. The anchor shows a still only because a mock has no player to
 * mount.
 */
export interface StreamingOccupant {
    kind: StageOccupantClass.STREAMING;
    /** Identity of what is playing, so a host can tell a swap from a re-render. */
    id: string;
    player?: React.ReactNode;
    /** Still or poster, shown until a player is handed over. */
    imageUrl?: string;
    /** Live now, versus a VOD. A STATE within this class — never a class of its own. */
    live: boolean;
    /** Pre-formatted. This layer does not decide a number's locale. */
    viewers?: string;
    title: string;
    subtitle?: string;
    tags?: readonly string[];
    /** Whether this stream supports the chat that EXPANDED exists to make room for. */
    canExpand?: boolean;
}

/**
 * Anything that is not a stream.
 *
 * `widget` is an opaque node for the same reason the column's occupants are:
 * this layer decides WHICH widget holds the slot, never what that widget
 * renders. D.4 builds the first narrow set to put here.
 */
export interface NonStreamingOccupant {
    kind: StageOccupantClass.NON_STREAMING;
    id: string;
    widget: React.ReactNode;
    /** Background art behind the widget, when it wants one. */
    imageUrl?: string;
}

export type StageOccupant = StreamingOccupant | NonStreamingOccupant;

/** The Stage, resolved: what is in it and how big it is. */
export interface ResolvedStage {
    occupant: StageOccupant | null;
    state: StageState;
    /**
     * True when the column should become chat. Derived here rather than
     * re-derived per host, because a host reading `state === EXPANDED` and
     * drawing its own conclusion is how the three implementations diverged in
     * the first place.
     */
    columnIsChat: boolean;
}

export interface ResolveStageInput {
    /** What the viewer selected, if anything. Null ⇒ the Stage is at rest. */
    streaming?: StreamingOccupant | null;
    /** What holds the Stage when nothing is playing. Null ⇒ the Stage is empty. */
    idle?: NonStreamingOccupant | null;
    /** The viewer asked to expand. Honoured only when a stream can actually expand. */
    expandRequested?: boolean;
}

/**
 * Resolve the Stage. ONE function, consumed by every host.
 *
 * Three mechanics existed before this — Drop Hub's `SurfaceStage`, Drop PAGE's
 * `CreatorSection` reflow, and Tournament's POV rail — built independently for
 * their own reasons. Nobody built the wrong thing; nothing forced the problem
 * to be solved once.
 */
export function resolveStage(input: ResolveStageInput): ResolvedStage {
    const {streaming, idle, expandRequested = false} = input;

    /*
     * A stream always wins the Stage.
     *
     * The non-streaming occupant is what holds the slot while nothing is
     * playing — it does not compete with a live stream for it. Same precedence
     * the roster already uses when a live member beats a clip.
     */
    if (streaming) {
        /*
         * EXPANDED is only reachable when the stream can support chat.
         *
         * Growing the Stage takes 60px from the column and turns it into a
         * chat room. Doing that for a stream with no chat to put there would
         * shrink the column and fill the space with nothing — which is why the
         * request is a REQUEST and this is where it is honoured or refused,
         * rather than a state a host can set directly. `canExpand` defaults to
         * true for a LIVE stream (the ordinary case, and what the anchor's own
         * Expanded control demonstrates) and false for a VOD, which has no
         * live chat by definition.
         */
        const canExpand = streaming.canExpand ?? streaming.live;
        const expanded = expandRequested && canExpand;
        return {
            occupant: streaming,
            state: expanded ? StageState.EXPANDED : StageState.WATCH,
            columnIsChat: expanded,
        };
    }

    /*
     * At rest. `idle ?? null` rather than throwing or substituting a
     * placeholder: a Stage with no non-streaming occupant configured is a
     * legitimate state — it is what every host does today — and the host
     * decides how to render an empty one.
     */
    return {
        occupant: idle ?? null,
        state: StageState.IDLE,
        columnIsChat: false,
    };
}

/** Narrowing helpers, so hosts test the class rather than sniffing for fields. */
export const isStreaming = (o: StageOccupant | null | undefined): o is StreamingOccupant =>
    o?.kind === StageOccupantClass.STREAMING;

export const isNonStreaming = (o: StageOccupant | null | undefined): o is NonStreamingOccupant =>
    o?.kind === StageOccupantClass.NON_STREAMING;
